// NexoraX Product Website Data Catalog
// Contains 50 distinct, high-fidelity premium hardware and digital assets.

export interface WebProduct {
  id: string;
  name: string;
  codename: string;
  category: 'Infrastructure' | 'Communications' | 'Neural' | 'Workspace' | 'Security';
  subcategory: string;
  description: string;
  price: number;
  badge: string;
  stars: number;
  reviews: number;
  features: string[];
  specs: Record<string, string>;
  isHot?: boolean;
  glowColor: string; // Tailwinds cyan, purple, lime, rose, amber, indigo, etc.
  modelUrl?: string;
  imageUrl?: string;
}

export const PRODUCT_CATEGORIES = [
  {
    name: 'Digital Infrastructure',
    id: 'Infrastructure',
    subcategories: ['Quantum Processing', 'Liquid Crypts', 'Fiber Transceivers', 'NexoraX Racks', 'Compute Blades']
  },
  {
    name: 'Communication Nodes',
    id: 'Communications',
    subcategories: ['Orbital Uplinks', 'Laser Bridges', 'Aura Routers', 'Multiplex Decoders', 'Frequency Anchors']
  },
  {
    name: 'Neural Systems',
    id: 'Neural',
    subcategories: ['NexoraX Visors', 'Synaptic Links', 'Haptic Command Controllers', 'Acoustic Earbuds', 'Vocalization Cells']
  },
  {
    name: 'Workspace Devices',
    id: 'Workspace',
    subcategories: ['Integrated Workstations', 'Tactile Pointers', 'Thermodynamic Keyboards', 'Ergonomic Pods', 'Hologram Arrays']
  },
  {
    name: 'NexoraX Security',
    id: 'Security',
    subcategories: ['Hardware Firewalls', 'Biometric Scanners', 'Air-gapped Ledgers', 'Shield Vaults', 'Chronos Sentinels']
  }
];

export const PRODUCTS_50: WebProduct[] = [
  // --- INFRASTRUCTURE (1-10) ---
  {
    id: 'prod-01',
    name: 'NexoraX Quantum Core Blade',
    codename: 'NEX-QCORE-X9',
    category: 'Infrastructure',
    subcategory: 'Quantum Processing',
    description: 'A 256-qubit cryo-cooled hardware blade featuring hyper-entangled superposition gates. Calibrated for instant ledger clearing and 0.8s transactional speed at absolute zero temperature.',
    price: 34999,
    badge: 'Director Choice',
    stars: 5.0,
    reviews: 124,
    features: ['256 NexoraX Qubits', 'Helium-3 Recirculating Cooler', 'Direct Secure Payment Provider API Routing Port'],
    specs: { 'Processing Power': '4.8 PetaFLOPS Quantum', 'Latency Line': '0.12ns Handshake', 'Form Factor': '1U NexoraX Rail' },
    isHot: true,
    glowColor: 'cyan'
  },
  {
    id: 'prod-02',
    name: 'Liquid Cryogenic Storage Crypt',
    codename: 'NEX-CRYP-V3',
    category: 'Infrastructure',
    subcategory: 'Liquid Crypts',
    description: 'An advanced fluorocarbon immersion tank engineered with dual recirculating hermetic compressors to sustain up to 12 server blades in a zero-dust, super-cooled state.',
    price: 18499,
    badge: 'High-Density Node',
    stars: 4.9,
    reviews: 86,
    features: ['Non-conductive Dielectric Fluid', 'Precision Thermal Sensors', 'Self-purging Gas Seal'],
    specs: { 'Thermal Capacity': '15,000 BTU/hr', 'Seal Integrity': 'Level-5 Military', 'Cooling Agent': 'NEX-ICE Fluorochemical' },
    glowColor: 'indigo'
  },
  {
    id: 'prod-03',
    name: 'Multiphasic Terabit Laser Receiver',
    codename: 'NEX-FIBER-T1',
    category: 'Infrastructure',
    subcategory: 'Fiber Transceivers',
    description: 'An optical transceiver leveraging multi-spectral laser arrays to deliver ultra-secure fiber links directly to institutional data distribution rings.',
    price: 6200,
    badge: 'Core Link',
    stars: 4.8,
    reviews: 95,
    features: ['10 Terabit throughput standard', 'Zero-leak light refraction core', 'Real-time telemetry diagnostics'],
    specs: { 'Fiber Protocol': 'FibreChannel SC9', 'Optical Pitch': '1310nm Dense Laser', 'Encasement': 'Aerospace Grade Al' },
    glowColor: 'purple'
  },
  {
    id: 'prod-04',
    name: 'Vanguard Structural Server Rack',
    codename: 'NEX-RACK-48U',
    category: 'Infrastructure',
    subcategory: 'NexoraX Racks',
    description: 'Heavyweight gold-plated 48U equipment frame engineered with electromagnetic shielding and active cooling rails connected directly to structural grounding grids.',
    price: 4500,
    badge: 'Industrial Frame',
    stars: 4.9,
    reviews: 62,
    features: ['Integrated Faraday isolation shielding', 'Pre-wired Node-01 telemetry arrays', 'Hydraulic leveling shock isolators'],
    specs: { 'Max Load': '2,400 lbs static', 'Rack Units': '48U Standard', 'Materials': 'Titanium Alloy Steel' },
    glowColor: 'amber'
  },
  {
    id: 'prod-05',
    name: 'NexoraX Compute Blade V12',
    codename: 'NEX-BLADE-V12',
    category: 'Infrastructure',
    subcategory: 'Compute Blades',
    description: 'A 256-core EPYC AMD localized server node engineered with CloudSync-02 integrated hardware level memory mirroring and state synchronization.',
    price: 12500,
    badge: 'Popular Compute',
    stars: 5.0,
    reviews: 147,
    features: ['256 Core AMD EPYC loops', 'CloudSync-02 Live Sync Core', 'ECC Memory parity correction'],
    specs: { 'Base Clock': '4.1 GHz Turbo', 'Memory Storage': '2.0TB DDR5 ECC', 'Cache Density': '1024MB L3' },
    isHot: true,
    glowColor: 'rose'
  },
  {
    id: 'prod-06',
    name: 'Imperial Multi-Tenant Hypervisor Host',
    codename: 'NEX-IMM-HOST',
    category: 'Infrastructure',
    subcategory: 'Quantum Processing',
    description: 'Hardware virtualization server optimized for zero-leak isolated sandboxes, preventing cross-tenant side-channel attacks across the 18-sector grid.',
    price: 21000,
    badge: 'Hyper-Secure',
    stars: 4.9,
    reviews: 73,
    features: ['Silicon-level logical container locks', 'Real-time hardware isolation tracking', 'Auto-recovery BIOS loops'],
    specs: { 'Virtual Nodes': 'NexoraX 1024 Max', 'Host Bandwidth': '400 Gbps Link', 'Audit Standard': 'ISO/IEC-NexoraX' },
    glowColor: 'lime'
  },
  {
    id: 'prod-07',
    name: 'Cryogenic Liquid Super-Pump Array',
    codename: 'NEX-PUMP-MAX',
    category: 'Infrastructure',
    subcategory: 'Liquid Crypts',
    description: 'Dual-stage magnetically levitated pump for high-capacity dielectric fluid recirculation. Zero heat dissipation into server environments.',
    price: 3400,
    badge: 'Accessory Engine',
    stars: 4.7,
    reviews: 44,
    features: ['Magnetic levitation impeller', 'Sub-8dB noise profile', 'Telemetry failure alerts'],
    specs: { 'Flow Velocity': '120 Liters/min', 'Bearing System': 'Super-conducting MagLev', 'Operational Volts': '110-240 AC Dual' },
    glowColor: 'emerald'
  },
  {
    id: 'prod-08',
    name: 'Glass-Wave Interconnect Core',
    codename: 'NEX-GLASS-X1',
    category: 'Infrastructure',
    subcategory: 'Fiber Transceivers',
    description: 'A passive light routing matrix containing 2048 individual optic intersections for Zero-Reflection local networking clusters.',
    price: 7800,
    badge: 'Passive Matrix',
    stars: 5.0,
    reviews: 31,
    features: ['Fused silica wave routing blocks', 'Zero active electric component footprint', 'Immune to EMI high-density spikes'],
    specs: { 'Signal Loss': 'Less than 0.01dB/km', 'Channels': '2048 Independent', 'Connectors': 'Multi-Fiber Push-on (MPO)' },
    glowColor: 'cyan'
  },
  {
    id: 'prod-09',
    name: 'Solid State Hyper-Throughput Vault',
    codename: 'NEX-VAULT-SSD',
    category: 'Infrastructure',
    subcategory: 'NexoraX Racks',
    description: 'A 500-Terabyte enterprise NVMe solid state array configured for high-frequency extraction cycles, backup mirroring, and database validation.',
    price: 15900,
    badge: 'Data Tank',
    stars: 4.8,
    reviews: 51,
    features: ['Continuous GDrive synchronization links', 'Self-encrypting hardware chips', 'Power-loss gold-plated super-capacitors'],
    specs: { 'Read Velocity': '28,000 MB/s Sequence', 'Write Velocity': '24,000 MB/s Sequence', 'Bus Architecture': 'PCIe Gen-6 Infinite' },
    glowColor: 'purple'
  },
  {
    id: 'prod-10',
    name: 'Vanguard Power Distribution Module',
    codename: 'NEX-PDU-SMART',
    category: 'Infrastructure',
    subcategory: 'Compute Blades',
    description: 'Intelligent power regulation hub that tracks power draws across nodes, instantly managing phase balancing and preventing localized surges.',
    price: 2900,
    badge: 'Grid Controller',
    stars: 4.9,
    reviews: 109,
    features: ['Over-current magnetic circuit breakers', 'Micro-second phase tracking', 'NexoraX Web monitoring panel'],
    specs: { 'Output capacity': '24.5kW Maximum', 'Inlet Phases': '3-Phase Delta 400V', 'Receptacles': '36 IEC-C13, 6 IEC-C19' },
    glowColor: 'amber'
  },

  // --- COMMUNICATIONS (11-20) ---
  {
    id: 'prod-11',
    name: 'Planet Telecom Satellite Router',
    codename: 'NEX-NET-AURA',
    category: 'Communications',
    subcategory: 'Aura Routers',
    description: 'A enterprise-grade orbital microwave transceiver routing general traffic, Resource resources, and priority customer nodes over secure Planet Telecom frequencies.',
    price: 5499,
    badge: 'Voice Verified',
    stars: 5.0,
    reviews: 141,
    features: ['Direct satellite link arrays', 'Zero jitter orbital signal alignment', 'Integrated 08000 622827 hot-wire line'],
    specs: { 'Carrier Bandwidth': '40 Ghps Direct Space', 'Security Cipher': '256-bit Cognitive OTP', 'Encryption Wave': 'Sub-carrier Pulse' },
    isHot: true,
    glowColor: 'rose'
  },
  {
    id: 'prod-12',
    name: 'Laser-Bridge High-Frequency Link',
    codename: 'NEX-LASER-H1',
    category: 'Communications',
    subcategory: 'Laser Bridges',
    description: 'Free-space optical laser communication transceiver operating at nanosecond speeds to secure direct physical connections between metropolitan centers.',
    price: 8900,
    badge: 'Line of Sight',
    stars: 4.8,
    reviews: 58,
    features: ['Autonomous target alignment laser', 'Fog and atmosphere penetration pulses', 'Physical interception self-kill circuits'],
    specs: { 'Max Distance': '15km Line-of-sight', 'Link Capacity': '100 Gbps Duplex', 'Laser Class': 'Class-4 NexoraX Invisible' },
    glowColor: 'cyan'
  },
  {
    id: 'prod-13',
    name: 'NexoraX Multi-Spectrum Uplink Beacon',
    codename: 'NEX-SAT-UPLINK',
    category: 'Communications',
    subcategory: 'Orbital Uplinks',
    description: 'Tri-band phase-array antenna engineered to track low-earth orbit constellations for robust, uninterruptible database sync channels.',
    price: 11200,
    badge: 'Antenna Array',
    stars: 4.9,
    reviews: 97,
    features: ['Phase-array instant beam tracking', 'All-weather extreme temp enclosure', 'Dual-redundant LNB converters'],
    specs: { 'Frequency Bands': 'Ku, Ka, and X-Band', 'Gain Index': '42.5 dBi Ka-Band', 'Survival Winds': 'Up to 150 MPH' },
    glowColor: 'purple'
  },
  {
    id: 'prod-14',
    name: 'Frequency Anchor & Phase Modulator',
    codename: 'NEX-ANCHOR-F7',
    category: 'Communications',
    subcategory: 'Frequency Anchors',
    description: 'Rubidium-standard atomic frequency reference that locks down communication pipelines to prevent spectrum drift under cognitive cyber strain.',
    price: 9500,
    badge: 'Time Sync',
    stars: 5.0,
    reviews: 64,
    features: ['Atomic Rubidium cell Core', '0.1 micro-second drift per year', 'Node-17 Chronos integration ready'],
    specs: { 'Sync Clock': '10 MHz Reference Output', 'Short Stability': 'Level-12 Allan Variance', 'Startup Calibration': 'Under 5m Lock' },
    glowColor: 'indigo'
  },
  {
    id: 'prod-15',
    name: 'Atmospheric Spatial Multiplex Decoder',
    codename: 'NEX-MULTIPLEX-S2',
    category: 'Communications',
    subcategory: 'Multiplex Decoders',
    description: 'Demultiplexes up to 64 laser signals traveling through complex turbulent corridors, reconstructing packet integrity without packet loss.',
    price: 13500,
    badge: 'Data Splitter',
    stars: 4.8,
    reviews: 43,
    features: ['64 Concurrent laser demux tracks', 'Dynamic atmospheric refractive correction', 'Low-power CMOS circuitry design'],
    specs: { 'Throughput Capacity': '1.28 Tbps total', 'Demodeling speed': '0.3s calibration', 'Optical Input Range': '-40dBm to +10dBm' },
    glowColor: 'lime'
  },
  {
    id: 'prod-16',
    name: 'Chronos Phase-Locked Sub-array',
    codename: 'NEX-CHRON-LINK',
    category: 'Communications',
    subcategory: 'Frequency Anchors',
    description: 'Synchronized radio-wave phase arrays designed to form a highly-directional communication beam directly to backup orbital nodes.',
    price: 14500,
    badge: 'Space Link',
    stars: 4.9,
    reviews: 19,
    features: ['Directional spatial beamforming', 'Anti-jam physical protection logic', 'Secure Payment Provider protocol physical encryption'],
    specs: { 'Targeting Offset': '< 0.001 Arcsec', 'Beam Width': '0.05 degrees pencil', 'Input Feed Power': '250W Continuous' },
    glowColor: 'emerald'
  },
  {
    id: 'prod-17',
    name: 'NexoraX Digital Aura Edge Gateway',
    codename: 'NEX-AURA-EDGE',
    category: 'Communications',
    subcategory: 'Aura Routers',
    description: 'A rackmount edge router featuring dedicated hardware ASIC modules designed to intercept and filter malicious regional IP routes.',
    price: 6800,
    badge: 'Secured Gateway',
    stars: 5.0,
    reviews: 74,
    features: ['Cognitive routing database', 'Physical status bypass relay switch', 'Real-time telemetry output streams'],
    specs: { 'Ports Standard': '16x 100G QSFP28', 'Route Capacity': '15 Million IPv4', 'Throughput Speed': '3.2 Tbps Switch' },
    glowColor: 'cyan'
  },
  {
    id: 'prod-18',
    name: 'NexoraX Laser Array Receiver Node',
    codename: 'NEX-LAS-REC',
    category: 'Communications',
    subcategory: 'Laser Bridges',
    description: 'Ground-based massive aperture optical receiver tailored to receive giga-bit signals under severe cloud environments.',
    price: 24700,
    badge: 'Station Core',
    stars: 4.9,
    reviews: 33,
    features: ['Adaptive optics deform mirror tracker', 'Liquid cooled telescope housing', 'Photomultiplier high-gain sensor'],
    specs: { 'Aperture Metric': '500mm diameter lens', 'Sensor Quantum Eff': '92% at 1550nm', 'Tracking Sweep': '360 Azimuth scan' },
    glowColor: 'purple'
  },
  {
    id: 'prod-19',
    name: 'Hyper-Orbit S-Band Array Beacon',
    codename: 'NEX-BEACON-S',
    category: 'Communications',
    subcategory: 'Orbital Uplinks',
    description: 'S-band telemetry beacon transmitting operational updates on active extraction models to orbital command systems.',
    price: 5200,
    badge: 'Active Uplink',
    stars: 4.7,
    reviews: 26,
    features: ['Compact light aerospace housing', 'Direct solar panel integration port', 'Automated health beacons'],
    specs: { 'Tx Frequency': '2.2 GHz S-Band', 'Power Output': '12W Peak RF power', 'Altitude Orbit': 'LEO aligned 500km' },
    glowColor: 'amber'
  },
  {
    id: 'prod-20',
    name: 'NexoraX Quantum Noise Modulator',
    codename: 'NEX-NOISE-MOD',
    category: 'Communications',
    subcategory: 'Multiplex Decoders',
    description: 'Hardware white noise generator injects localized random quantum vibrations into cables to mask physical electromagnetic emission leaks.',
    price: 3950,
    badge: 'Passive Guard',
    stars: 4.8,
    reviews: 48,
    features: ['True thermodynamic quantum entropy core', 'Dimmable noise power levels', 'Unpredictable random modulation seed'],
    specs: { 'Output Noise Band': '50 Hz to 18 GHz', 'Random entropy rate': '4.0 Gbps pure', 'Power Draw': '15W standard' },
    glowColor: 'teal'
  },

  // --- NEURAL SYSTEMS (21-30) ---
  {
    id: 'prod-21',
    name: 'NexoraX VR-X1 Neural Visor',
    codename: 'NEX-VR-NODE-X1',
    category: 'Neural',
    subcategory: 'NexoraX Visors',
    description: 'The premier neural headset featuring dual-eye 4K micro-OLED rendering and direct occipital sensor arrays. Transports any administrator into an immersive three-dimensional spatial Command Deck.',
    price: 1299,
    badge: 'Out of Universe',
    stars: 5.0,
    reviews: 188,
    features: ['Holographic 18-sector spatial projection', 'Occipital telemetry interface triggers', '0.8s laser distance eye tracking'],
    specs: { 'Panel Tech': 'Micro-OLED Dual Panel', 'Refresh Frequency': '144Hz Constant', 'Field of Vision': '135 Deg Immersive' },
    isHot: true,
    glowColor: 'purple'
  },
  {
    id: 'prod-22',
    name: 'Aries Synaptic Deep-Link Band',
    codename: 'NEX-SYN-BAND',
    category: 'Neural',
    subcategory: 'Synaptic Links',
    description: 'A light, carbon-fiber headband that tracks micro-vibrations and electromagnetic neural patterns to translates thoughts into direct terminal controls.',
    price: 2490,
    badge: 'Mind Controller',
    stars: 5.0,
    reviews: 110,
    features: ['64-channel premium EEG sensor matrix', 'Pre-trained AI gesture command maps', 'Recharges via wireless inductors'],
    specs: { 'Latency Delay': '0.08s Thought-to-click', 'Materials': 'Frictionless Carbon Fiber', 'Battery Stand': '18 Hrs Active' },
    glowColor: 'cyan'
  },
  {
    id: 'prod-23',
    name: 'NexoraX Prime Acoustic Earbuds v2',
    codename: 'NEX-EARBUDS-V2',
    category: 'Neural',
    subcategory: 'Acoustic Earbuds',
    description: 'High-fidelity audio feedback node featuring state-of-the-art active noise cancelling to isolate administrative thoughts from external noise.',
    price: 249,
    badge: '3D Sound',
    stars: 4.9,
    reviews: 320,
    features: ['Ultra-dense active noise exclusion chamber', 'Gold-plated 12mm speaker membranes', 'Sub-bass pressure equalizers'],
    specs: { 'Acoustic response': '4 Hz to 42,000 Hz', 'ANC Isolation': '-48dB Ambient Filter', 'Audio Codecs': 'LDAC, aptX Lossless' },
    isHot: true,
    glowColor: 'rose'
  },
  {
    id: 'prod-24',
    name: 'Interactive Bio-Haptic Feedback Glove',
    codename: 'NEX-GLOVE-HAPTIC',
    category: 'Neural',
    subcategory: 'Haptic Command Controllers',
    description: 'Command gloves utilizing localized micro-vibration solenoid arrays to provide physical feedback when touching simulated 3D interfaces.',
    price: 1199,
    badge: 'Tactile Link',
    stars: 4.8,
    reviews: 83,
    features: ['Solenoid micro-pressure points on tips', 'Flex-grade structural Kevlar mesh', 'Threaded real-time drift loops'],
    specs: { 'Force Feedback': 'Up to 2.5N per finger', 'Tracking Rate': '240Hz spatial sweep', 'Connection': 'Ultra-low lat Wireless' },
    glowColor: 'emerald'
  },
  {
    id: 'prod-25',
    name: 'Nexorax Vocalization Cell Capsule',
    codename: 'NEX-VOCAL-CELL',
    category: 'Neural',
    subcategory: 'Vocalization Cells',
    description: 'Premium tabletop voice synthesis capture capsule containing multi-diaphragm gold-sputtered condenser loops tailored to model AI voice files.',
    price: 850,
    badge: 'Studio Microphone',
    stars: 4.9,
    reviews: 104,
    features: ['Triple 34mm gold-sputtered capsules', 'Switchable polar pickup direction patterns', 'Class-A discrete pre-amp circuits'],
    specs: { 'S/N Ratio': '88 dBA High Quality', 'Max SPL': '140 dB Sound Limit', 'Connection standard': 'NexoraX USB-C / XLR' },
    glowColor: 'amber'
  },
  {
    id: 'prod-26',
    name: 'Occipital Multi-Channel Telemetry Core',
    codename: 'NEX-OCCIPITAL-S5',
    category: 'Neural',
    subcategory: 'Synaptic Links',
    description: 'Interface box targeting visual cortex telemetry, projecting database telemetry data directly onto a user\'s optical field.',
    price: 5800,
    badge: 'Visual Injector',
    stars: 5.0,
    reviews: 15,
    features: ['Laser micro-projectors with eye tracking', 'Adjustable pupil distance loops', 'Direct Node-06 diagnostic support'],
    specs: { 'Projection Density': '1080p spatial virtual', 'Refresh Loop': '120Hz tracking refresh', 'Luminous Flux': '500 Lumens Max shadow' },
    glowColor: 'indigo'
  },
  {
    id: 'prod-27',
    name: 'Prime Acoustic Earbuds Minimalist Edition',
    codename: 'NEX-EARBUDS-MIN',
    category: 'Neural',
    subcategory: 'Acoustic Earbuds',
    description: 'A lighter, highly discrete variant of our biometric earbuds designed for executive travel and long negotiation calls.',
    price: 189,
    badge: 'Sentry Spec',
    stars: 4.7,
    reviews: 191,
    features: ['Ultra-low profile micro-capsule shell', 'Sweat and dust-proof IP67 rating', 'One-touch emergency frequency lock'],
    specs: { 'Driver Unit': '6mm Dynamic Driver', 'Battery Life': '12 hrs standalone case', 'Transmit Distance': '30m NexoraX stable' },
    glowColor: 'cyan'
  },
  {
    id: 'prod-28',
    name: 'Command Haptic Trackpad Board',
    codename: 'NEX-TRACK-BOARD',
    category: 'Neural',
    subcategory: 'Haptic Command Controllers',
    description: 'A glass haptic touchpad supporting complex 10-finger hand movements for navigation of the 18-sector spatial matrices.',
    price: 499,
    badge: 'Device Input',
    stars: 4.9,
    reviews: 74,
    features: ['NexoraX grade glass cursor plate', '96 distinct physical actuator nodes', 'Customizable feedback vibration waves'],
    specs: { 'Active Zone': '12x8 inch surface', 'Input Precision': '0.01mm touch accuracy', 'Latency Rate': '1000Hz USB sample' },
    glowColor: 'purple'
  },
  {
    id: 'prod-29',
    name: 'NexoraX Headset Elite Stand',
    codename: 'NEX-STAND-H1',
    category: 'Neural',
    subcategory: 'NexoraX Visors',
    description: 'Display and rapid charging dock for the VR-X1 node headgear. Composed of CNC machined space aluminum with emerald ambient glows.',
    price: 299,
    badge: 'Workspace Gem',
    stars: 4.8,
    reviews: 42,
    features: ['Inductive rapid charging systems', 'Visible status logo display plate', 'Weighted non-slip carbon rubber footers'],
    specs: { 'Charging Speed': '15W rapid wireless', 'Materials': 'Aerospace Carbon Aluminum', 'Glow Intensity': 'Adjustable 0-100%' },
    glowColor: 'emerald'
  },
  {
    id: 'prod-30',
    name: 'Executive Vocalizer Sound Chamber',
    codename: 'NEX-CHAMBER-V',
    category: 'Neural',
    subcategory: 'Vocalization Cells',
    description: 'A miniature acoustic foam desktop chamber that isolates microphones from desktop vibration, securing pure vocal samples.',
    price: 350,
    badge: 'Acoustic Guard',
    stars: 4.9,
    reviews: 53,
    features: ['High-density open-cell acoustic foam', 'Laser etched metallic backplate', 'Integrated universal mic mount bracket'],
    specs: { 'Acoustic Absorbing': 'NRC 0.92 premium', 'Weight': '2.4 lbs lightweight', 'Enclosure Space': '14x12x10 inches' },
    glowColor: 'lime'
  },

  // --- WORKSPACE DEVICES (31-40) ---
  {
    id: 'prod-31',
    name: 'NexoraX Mega Madz Pro Controller v2',
    codename: 'NEX-CTRL-MADZ-V2',
    category: 'Workspace',
    subcategory: 'Haptic Command Controllers',
    description: 'Our top-tier executive input gamepad adorned with hand-stitched leather, tactile mechanical microswitches, and a gorgeous purple interactive display ring.',
    price: 299,
    badge: 'NexoraX Elite',
    stars: 5.0,
    reviews: 215,
    features: ['Contactless Hall-effect joy shafts', '3-way adjustable trigger mechanical stops', 'Vanguard-Closer quick-link trigger button'],
    specs: { 'Button Durability': '50 Million Actuations', 'Polling Rate': '1000Hz direct USB-C', 'Wireless Connectivity': '2.4GHz Zero latency' },
    isHot: true,
    glowColor: 'indigo'
  },
  {
    id: 'prod-32',
    name: 'Asus ROG Gladius Active-Cooling Mouse v2',
    codename: 'NEX-MOUSE-GLAD-V2',
    category: 'Workspace',
    subcategory: 'Tactile Pointers',
    description: 'The premier tactical mouse built with carbon shell fibers and a microscopic ventilation blower fan to keep administrative palms bone dry.',
    price: 149,
    badge: 'Best Seller',
    stars: 4.9,
    reviews: 320,
    features: ['36,000 DPI AimPoint optical glass sensor', 'Integrated active-cooling thermodynamic fan', 'Detachable paracord dynamic cable'],
    specs: { 'Sensor Tracking': '650 IPS speed max', 'Accelerating Capacity': '50G high inertia', 'Switch Life': '90 Million clicks' },
    glowColor: 'emerald'
  },
  {
    id: 'prod-33',
    name: 'Asus ROG Zephyrus G14 NexoraX Computer',
    codename: 'NEX-LAPTOP-ZEPH-G14',
    category: 'Workspace',
    subcategory: 'Integrated Workstations',
    description: 'A hyper-portable computer wrapped in machined dark-alloy armor, optimized to deploy and debug decentralized software nodes on the fly.',
    price: 1899,
    badge: 'Tactical Node',
    stars: 4.9,
    reviews: 142,
    features: ['Ryzen 9 ultra-threaded computing loop', 'NVIDIA RTX 4090 laptop physics core', 'CloudSync-02 background backup software'],
    specs: { 'CPU Cores': '16 Cores / 32 Threads CPU', 'System RAM': '64GB dual-channel DDR5', 'Display Panel': '14-inch ROG Nebula OLED' },
    isHot: true,
    glowColor: 'cyan'
  },
  {
    id: 'prod-34',
    name: 'Dell Alienware Aurora Infinite Desktop v2',
    codename: 'NEX-AW-AURORA-INF-V2',
    category: 'Workspace',
    subcategory: 'Integrated Workstations',
    description: 'An aggressive dual-chamber gaming and data extraction workstation utilizing premium liquid-nitrogen ready blocks and customized neon aesthetics.',
    price: 2499,
    badge: 'Power Rig',
    stars: 4.8,
    reviews: 96,
    features: ['Cryogenic fluid-cooling system', 'Machined transparent aerospace side plate', 'Node-17 Chronos Ledger local cache link'],
    specs: { 'System Cache': '256MB high-speed LLC', 'Power Regulatory': '1350W 80-Plus Platinum', 'GPU Hardware': 'GeForce RTX 4590 Tier' },
    glowColor: 'purple'
  },
  {
    id: 'prod-35',
    name: 'NexoraX ErgoSpace Gas-Lift Command Chair',
    codename: 'NEX-ERGO-CHAIR',
    category: 'Workspace',
    subcategory: 'Ergonomic Pods',
    description: 'A luxury orthopedic office throne fitted with a custom non-slip memory mesh, active spinal support brackets, and multi-axis adjustable hand rests.',
    price: 999,
    badge: 'VIP Comfort',
    stars: 4.8,
    reviews: 53,
    features: ['Active-balance hydraulic support piston', 'Frictionless dual gas-lift cylinders', 'Interlocked mechanical head stabilization'],
    specs: { 'Swell Weight Limit': '450 lbs high capacity', 'Tilt Amplitude': '135 degrees recline', 'Mesh Fabric': 'Aerotherm Breathable' },
    glowColor: 'cyan'
  },
  {
    id: 'prod-36',
    name: 'Thermodynamic Solid-Brass Command Keyboard',
    codename: 'NEX-KEYBOARD-BRASS',
    category: 'Workspace',
    subcategory: 'Thermodynamic Keyboards',
    description: 'A monumental mechanical typing deck crafted from a single slab of solid marine brass. Fitted with mechanical switches and customized keys representing NexoraX functions.',
    price: 650,
    badge: 'Crafted Piece',
    stars: 5.0,
    reviews: 38,
    features: ['Individually milled solid brass body', 'Hot-swappable mechanical linear keys', 'Double physical key gaskets, silent profile'],
    specs: { 'Plate Weight': '9.4 lbs substantial', 'Interface Link': 'Detachable braided Type-C', 'Lighting Matrix': 'Custom amber per-key glow' },
    glowColor: 'amber'
  },
  {
    id: 'prod-37',
    name: 'Hologram-Beaming Desktop Projection Matrix',
    codename: 'NEX-HOLO-BEAM',
    category: 'Workspace',
    subcategory: 'Hologram Arrays',
    description: 'A desktop beacon that projects three-dimensional animated icons and live data charts directly above your physical office desk.',
    price: 1500,
    badge: 'Holo Array',
    stars: 4.9,
    reviews: 49,
    features: ['Laser light array refraction prisms', 'Interactive hand gesture tracking system', 'Synchronizes with real-time browser states'],
    specs: { 'Aperture Peak': '50W projection lens', 'Render Density': '5 Million voxel points', 'Tracking Sweepers': 'Ultrasonic spatial sensors' },
    glowColor: 'indigo'
  },
  {
    id: 'prod-38',
    name: 'Tactile Dial Master Sound Level Node',
    codename: 'NEX-DIAL-AUDIO',
    category: 'Workspace',
    subcategory: 'Tactile Pointers',
    description: 'Heavyweight rotating aluminum dial tailored for immediate adjustment of active telephone volumes and AI voice synthesis streams.',
    price: 199,
    badge: 'Control Knob',
    stars: 4.9,
    reviews: 81,
    features: ['Machined aerospace grade black alloy', 'Magnetic click tension adjustment dial', 'Direct LED volume display track'],
    specs: { 'Resolution Click': '24 steps per full spin', 'Connect Cable': 'Braided Type-C custom', 'Bottom Surface': 'Sticky micro-suction silicone' },
    glowColor: 'cyan'
  },
  {
    id: 'prod-39',
    name: 'Ergonomic Desktop Leather Wrist Pad',
    codename: 'NEX-WRIST-PAD',
    category: 'Workspace',
    subcategory: 'Ergonomic Pods',
    description: 'Memory-foam filled leather desktop pad designed to keep wrists supported during intense server deployment campaigns.',
    price: 89,
    badge: 'Desk Comfort',
    stars: 4.8,
    reviews: 132,
    features: ['Real premium full-grain leather', 'Cold-cure memory foam inner core', 'Stitched with double thread accents'],
    specs: { 'Dimensions': '18 x 3.5 x 0.8 inches', 'Base Material': 'Suede micro-grip bottom', 'Clean Support': 'Easy-wipe surface layer' },
    glowColor: 'slate'
  },
  {
    id: 'prod-40',
    name: 'Tactile Thermodynamic Macropad Controller',
    codename: 'NEX-MACRO-PAD',
    category: 'Workspace',
    subcategory: 'Thermodynamic Keyboards',
    description: 'A compact 12-key macro pad configured with individual OLED keys to display active command icons representing server assets.',
    price: 320,
    badge: 'Speed Typist',
    stars: 4.9,
    reviews: 67,
    features: ['12 individual OLED mechanical keys', 'Continuous layout configuration GUI', 'Rotary encoder knob integrated'],
    specs: { 'Key Specs': '64x64 tiny digital displays', 'Macro Memory': '32MB on-board chip storage', 'Switches': 'Gateron Custom linear keys' },
    glowColor: 'teal'
  },

  // --- NEXORAX SECURITY (41-50) ---
  {
    id: 'prod-41',
    name: 'CloudSync-02 Air-Gapped Firewall Node',
    codename: 'NEX-SHIELD-V5',
    category: 'Security',
    subcategory: 'Hardware Firewalls',
    description: 'A massive rackmount encryption firewall containing specialized ASIC network chips designed to inspect incoming packets and isolate dangerous IP addresses in 0.8s.',
    price: 14500,
    badge: 'NexoraX Shield',
    stars: 5.0,
    reviews: 198,
    features: ['enterprise-grade high-volume packet inspection', 'Physically separated dry-relay isolation switch', 'Automated Daily Ledger synchronization'],
    specs: { 'Throughput Rate': '800 Gbps Deep Scan', 'System latency': '0.04 milliseconds filter', 'Form Factor': '2U Secure Chassis' },
    isHot: true,
    glowColor: 'red'
  },
  {
    id: 'prod-42',
    name: 'Biometric Iris Recognition Scanner',
    codename: 'NEX-SCAN-IRIS',
    category: 'Security',
    subcategory: 'Biometric Scanners',
    description: 'A high-definition infrared optical iris scanner optimized to verify administrator credentials and instantly lock servers when an un-vetted user is detected.',
    price: 4800,
    badge: 'Biometric Lock',
    stars: 5.0,
    reviews: 114,
    features: ['Multi-spectrum laser iris scan', '0.8s secure identity verification', 'CloudSync-02 NexoraX Resource Library integration'],
    specs: { 'False Match Rate': '1 in 10 Million scans', 'Scan Distance': '12 to 24 inches range', 'Cam Resolution': '50 Megapixel high depth' },
    isHot: true,
    glowColor: 'cyan'
  },
  {
    id: 'prod-43',
    name: 'High-Fidelity Air-Gapped Ledger Key',
    codename: 'NEX-LEDGER-CHRONOS',
    category: 'Security',
    subcategory: 'Air-gapped Ledgers',
    description: 'A credit-card sized cryptographic hardware key calibrated with a military atomic clock chip, generating un-copyable local authorization codes.',
    price: 899,
    badge: 'Hard Key',
    stars: 4.9,
    reviews: 245,
    features: ['Rubidium microtime secure key generator', 'Waterproof shock-proof resin chassis', 'Self-destruct loop under high pressure'],
    specs: { 'Crypto Standard': 'AES-256 GCM Secure Block', 'Display Type': 'Bi-stable electronic paper', 'Hardware life': '5 yrs battery cell' },
    glowColor: 'amber'
  },
  {
    id: 'prod-44',
    name: 'Vanguard Biometric Fingerprint Locker',
    codename: 'NEX-LOCK-FINGER',
    category: 'Security',
    subcategory: 'Biometric Scanners',
    description: 'Heavy solid-steel desktop biometric drawer designed to store hardware keys and print materials, unlocking exclusively via finger vein signature.',
    price: 1250,
    badge: 'Steel Vault',
    stars: 4.8,
    reviews: 62,
    features: ['Interactive finger-vein infrared sensor', 'Tension steel deadbolt locking vectors', 'Dual USB backup logging ports'],
    specs: { 'Steel Thickness': '10mm solid aerospace alloy', 'Bolt Diameter': '24mm massive steel bolts', 'Verification Delay': '0.3s secure release' },
    glowColor: 'emerald'
  },
  {
    id: 'prod-45',
    name: 'NexoraX Multi-Spectrum Jammer Node',
    codename: 'NEX-JAM-WAVE',
    category: 'Security',
    subcategory: 'Shield Vaults',
    description: 'A physical device emitting high-density localized white noise to disrupt wireless microphones and listening modules, locking down nearby conference spaces.',
    price: 3200,
    badge: 'Room Shield',
    stars: 4.9,
    reviews: 41,
    features: ['Active RF signal shielding field', 'Intelligent signal strength sensors', 'Rechargeable internal emergency battery'],
    specs: { 'Shield Radius': '25 meters bubble space', 'Jamming Range': '800-6000 MHz spectrum', 'Continuous Play': '8 Hrs standalone' },
    glowColor: 'indigo'
  },
  {
    id: 'prod-46',
    name: 'Airbar Multi-Aperture Laser Curtains',
    codename: 'NEX-CURTAIN-LAS',
    category: 'Security',
    subcategory: 'Shield Vaults',
    description: 'Physical security boundary emitting an invisible grid of infrared laser beams over doorways, logging entry attempt times into the Chronos Ledger.',
    price: 5400,
    badge: 'Laser Barrier',
    stars: 4.9,
    reviews: 29,
    features: ['24 invisible laser protection grid lines', 'Sub-millisecond interruption sensors', 'Automatic visual camera record trigger'],
    specs: { 'Grid Height': '8.2 feet standard beam', 'Operating Spectrum': '940nm secure infrared', 'Output Relay': 'Dry contacts secure link' },
    glowColor: 'purple'
  },
  {
    id: 'prod-47',
    name: 'NexoraX Signal Sealing Vault Cabinet',
    codename: 'NEX-VAULT-CAB',
    category: 'Security',
    subcategory: 'Shield Vaults',
    description: 'A massive heavy steel cabinet with conductive physical seals that blocks all incoming and outgoing wireless signals.',
    price: 9800,
    badge: 'Secure Storage',
    stars: 5.0,
    reviews: 18,
    features: ['Conductive copper perimeter gaskets', 'Keyed biometric multi-lever lock', 'Integrated internal non-wireless sensor probes'],
    specs: { 'RF Attenuation': '>120dB over 10GHz span', 'Chassis Volume': '18 Cubic Feet storage', 'Cabinet Weight': '850 lbs non-mobile' },
    glowColor: 'slate'
  },
  {
    id: 'prod-48',
    name: 'Vanguard Cognitive Threat Sentinel',
    codename: 'NEX-THREAT-SENTINEL',
    category: 'Security',
    subcategory: 'Chronos Sentinels',
    description: 'Advanced rackmount system monitor that scans outgoing TCP packets to isolate unauthorized data leakage vectors in real time.',
    price: 13500,
    badge: 'Threat Hunter',
    stars: 4.9,
    reviews: 55,
    features: ['Silicon deep packet matching loops', 'Automatic GDrive security override relays', 'Secure Payment Provider payment physical lock links'],
    specs: { 'Data Scan Rate': '400 Gbps deep stream', 'Filter Library': 'NexoraX Threat DB v2', 'Auto-isolate latency': '24 microseconds' },
    glowColor: 'red'
  },
  {
    id: 'prod-49',
    name: 'NexoraX USB-C Air-Gap Block Link',
    codename: 'NEX-USB-GAP',
    category: 'Security',
    subcategory: 'Air-gapped Ledgers',
    description: 'A physical inline adapter that strips power lines and intercepts unvetted data spikes to secure physical hardware testing.',
    price: 120,
    badge: 'Handy Tool',
    stars: 4.8,
    reviews: 172,
    features: ['Physical data lines disconnect switch', 'Hardware isolation LED status tracks', 'Premium aluminum shield housing casing'],
    specs: { 'Data Speed': '480 Mbps safe limited', 'Enclosure': 'Anodized gold defense metal', 'Rating Max': '100W power delivery line' },
    glowColor: 'lime'
  },
  {
    id: 'prod-50',
    name: 'NexoraX Quantum Key Distribution Host',
    codename: 'NEX-QKD-HOST',
    category: 'Security',
    subcategory: 'Hardware Firewalls',
    description: 'Provides physical photon quantum mechanics polarization encryption seeds to dynamically swap encryption keys thousands of times per minute.',
    price: 42000,
    badge: 'Singularity Guard',
    stars: 5.0,
    reviews: 8,
    features: ['Single-photon avalanche laser emitter', 'Dynamic laser alignment optical mirror', 'Zero-intercept crypto verification loop'],
    specs: { 'Secure Key Velocity': '25,000 keys per second', 'Fiber Length Max': '120 km point-to-point', 'Laser wavelength': '1550.12nm' },
    isHot: true,
    glowColor: 'purple'
  },
  {
    id: 'prod-4101',
    name: 'Bio-Link Smart Ring',
    codename: 'NX-4101',
    category: 'Neural',
    subcategory: 'Neural Systems',
    description: 'Continuous biometric telematics sync via low-latency local radio protocols.',
    price: 330,
    badge: 'SYS_REF // NX-4101',
    stars: 5.0,
    reviews: 124,
    features: ['Continuous biometric telemetry stream', 'High-conductivity alloy surface contact', '0.08s heartbeat calibration matrix'],
    specs: { 'Telemetry Stream': '0.08s Realtime Sync', 'Crypto Node': '0.42 ETH Ledger Integration', 'Wireless Standard': 'Low Latency Local Radio Protocol' },
    isHot: true,
    glowColor: 'cyan'
  }
];
